import { useEffect, useState, useCallback, useRef } from "react";
import PubSub from "../../services/PubSub";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";


const UNLOCK_DURATION = 15 * 60 * 1000; // 15 minutes of unlock
const REFRESH_INTERVAL = 0.5 * 60 * 1000; // Refresh every 30 seconds.

const getUnlockedDeploymentsFromStorage = () => {
    const stored = window.sessionStorage.getItem("unlockedDeployments") || "{}";
    return JSON.parse(stored);
}

const checkDeploymentIsUnlocked = (deploymentId) => {
    const unlockedDeployments = getUnlockedDeploymentsFromStorage();

    const timestamp = unlockedDeployments?.[deploymentId];
    if (!timestamp) return false;

    const isExpired = (Date.now() - timestamp) > UNLOCK_DURATION;
    return !isExpired;
};

const useUnlockedDeployments = () => {
    const [translate] = useTranslation();
    const { id: currentDeploymentId } = useParams();
    const prevDeploymentRef = useRef(currentDeploymentId);
    const [unlockedDeployments, _setUnlockedDeployments] = useState(getUnlockedDeploymentsFromStorage());
    const [, setShownWarnings] = useState({});

    const unlockDeployment = useCallback((deploymentId) => {
        const updated = { ...unlockedDeployments, [deploymentId]: Date.now() };
        window.sessionStorage.setItem("unlockedDeployments", JSON.stringify(updated));

        _setUnlockedDeployments(() => {
            // Reset shown warnings for this deploymentId.
            setShownWarnings(currentWarnings => ({
                ...currentWarnings,
                [deploymentId]: null
            }));
            return updated;
        });

        PubSub.publish("refresh-unlocked-deployments");
    }, [unlockedDeployments]);

    const lockDeployment = useCallback((deploymentId) => {
        const updated = { ...unlockedDeployments };
        delete updated[deploymentId];
        window.sessionStorage.setItem("unlockedDeployments", JSON.stringify(updated));
        _setUnlockedDeployments(updated);
        PubSub.publish("refresh-unlocked-deployments");
    }, [unlockedDeployments]);

    const isUnlocked = useCallback((deploymentId) => {
        const unlocked = checkDeploymentIsUnlocked(deploymentId);

        // If expired, trigger cleanup.
        if (!unlocked && unlockedDeployments[deploymentId]) {
            lockDeployment(deploymentId);
        }

        return unlocked;
    }, [unlockedDeployments, lockDeployment]);

    const unlockedTimeRemaining = useCallback((deploymentId) => {
        if (!isUnlocked(deploymentId)) return null;

        const unlockedAt = unlockedDeployments?.[deploymentId];
        if (!unlockedAt) return null;

        const now = Date.now();
        const elapsed = now - unlockedAt;
        const remaining = UNLOCK_DURATION - elapsed;

        if (remaining <= 0) return null;

        // Convert ms to seconds.
        const totalSeconds = Math.floor(remaining / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        // Format the string based on what units are needed
        const parts = [];
        if (hours > 0) {
            parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
        }
        if (minutes > 0) {
            parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
        }
        if ((minutes < 1 && seconds > 0) || parts.length === 0) {
            parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`);
        }

        return parts.join(', ');
    }, [unlockedDeployments, isUnlocked]);

    useEffect(() => {
        const prevId = prevDeploymentRef.current;

        // If we had a previous deployment and it is different from the current one.
        if (prevId && prevId !== currentDeploymentId) {
            if (isUnlocked(prevId)) lockDeployment(prevId);
        }

        prevDeploymentRef.current = currentDeploymentId;
    }, [currentDeploymentId, isUnlocked, lockDeployment]);

    const showTimeToast = useCallback(({ deploymentId, minutes, type = "warning", hideAfter }) => {
        let msgText;
        if (minutes <= 0) msgText = translate("DEPLOYMENT_UNLOCK_EXPIRED");
        if (minutes === 1) msgText = translate("DEPLOYMENT_UNLOCK_1_MINUTE");
        if (minutes > 1) msgText = translate("DEPLOYMENT_UNLOCK_MINUTES", { minutes });

        const message = <div>
            <div className="text-secondary">
                {msgText}
            </div>
            <div className="ms-auto">
                <span
                    className="text-primary fw-semibold text-decoration-underline clickable"
                    onClick={() => {
                        PubSub.publish("deployment-unlock-timer-hide");
                        unlockDeployment(deploymentId);
                    }}
                >
                    {minutes > 0 ? translate("OTM_EXTEND_UNLOCK_TIMER") : translate("OTM_UNLOCK")}
                </span>
            </div>
        </div>

        if (minutes < 0) { // Previous toast was the 1 minute remaining, hide it manually.
            PubSub.publish("deployment-unlock-timer-hide");
        }
        setTimeout(() => {
            PubSub.publish("deployment-unlock-timer-show", {
                title: translate("OTM_WARNING"),
                message,
                type,
                autohide: minutes > 0,
                css: {
                    container: "border-2",
                    header: minutes <= 1 ? "bg-danger-subtle" : "bg-warning-subtle",
                    title: "text-secondary"
                }
            })

            if (hideAfter) setTimeout(() => PubSub.publish("deployment-unlock-timer-hide"), hideAfter);
        }, minutes >= 1 ? 100 : 3000)
    }, [unlockDeployment, translate]);

    const cleanupExpired = useCallback(() => {
        const now = Date.now();
        const updated = {};

        Object.entries(unlockedDeployments).forEach(([id, timestamp]) => {
            const elapsed = now - timestamp;
            const remaining = UNLOCK_DURATION - elapsed;

            if (remaining > 0) {
                const minutesLeft = Math.ceil(remaining / 60000);

                // Configurable warning rules
                const warningConfig = {
                    5: 30000, // at 5 minute left, show for 30 seconds.
                    3: 30000, // at 3 minute left, show for 30 seconds.
                    1: -1, // at 1 minute left, show always.
                }

                if (warningConfig[minutesLeft]) {
                    setShownWarnings(currentWarnings => {
                        const alreadyShown = currentWarnings?.[id]?.[minutesLeft];
                        if (!alreadyShown) {
                            showTimeToast({
                                deploymentId: id,
                                minutes: minutesLeft,
                                hideAfter: warningConfig[minutesLeft] > 0 ? warningConfig[minutesLeft] : null
                            });

                            return {
                                ...currentWarnings,
                                [id]: {
                                    ...currentWarnings?.[id],
                                    [minutesLeft]: true
                                }
                            };
                        }

                        return currentWarnings;
                    });
                }

                updated[id] = timestamp;
            } else {
                // timer has ran out
                showTimeToast({
                    deploymentId: id,
                    minutes: 0,
                    hideAfter: null
                });
            }
        });

        window.sessionStorage.setItem("unlockedDeployments", JSON.stringify(updated));
        _setUnlockedDeployments(updated);

        PubSub.publish("refresh-unlocked-deployments");
    }, [unlockedDeployments, showTimeToast, setShownWarnings]);

    useEffect(() => {
        const interval = setInterval(cleanupExpired, REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, [cleanupExpired]);

    useEffect(() => {
        const id = PubSub.subscribe("deployment-unlock-timer-refresh", ({ deploymentId }) => {
            if (isUnlocked(deploymentId)) {
                unlockDeployment(deploymentId);
            }
        })

        return () => PubSub.unsubscribe("deployment-unlock-timer-refresh", id);
    }, [isUnlocked, unlockDeployment])

    return { unlockedDeployments, unlockDeployment, lockDeployment, isUnlocked, unlockedTimeRemaining, showTimeToast };
};

const useDeploymentStages = () => {
    const [deploymentStages, _setDeploymentStages] = useState(() => {
        const stored = window.sessionStorage.getItem("deploymentStages") || "{}";
        return JSON.parse(stored);
    });

    const setDeploymentStage = useCallback((deploymentId, stage) => {
        _setDeploymentStages((prev) => {
            const updated = { ...prev, [deploymentId]: stage };
            window.sessionStorage.setItem("deploymentStages", JSON.stringify(updated));
            return updated;
        });
    }, []);

    const getDeploymentStage = useCallback((deploymentId) => {
        return deploymentStages[deploymentId];
    }, [deploymentStages]);

    const clearDeploymentStages = useCallback(() => {
        window.sessionStorage.removeItem("deploymentStages");
        _setDeploymentStages({});
    }, []);

    return { deploymentStages, setDeploymentStage, getDeploymentStage, clearDeploymentStages };
};

const getDeploymentStageFromStorage = (deploymentId) => {
    const stored = window.sessionStorage.getItem("deploymentStages") || "{}";
    const deploymentStages = JSON.parse(stored);
    return deploymentStages[deploymentId];
};

export {
    getUnlockedDeploymentsFromStorage,
    checkDeploymentIsUnlocked,
    useUnlockedDeployments,
    useDeploymentStages,
    getDeploymentStageFromStorage
};